import { useCallback, useEffect, useMemo, useState } from 'react';

import type { UsePosUiState } from './usePosUiState';

const MOBILE_BREAKPOINT = 992;

interface UsePosLayoutArgs {
  layoutMode: UsePosUiState['layoutMode'];
  setLayoutMode: UsePosUiState['setLayoutMode'];
  bottomBarPreferences: UsePosUiState['bottomBarPreferences'];
  setBottomBarPreferences: UsePosUiState['setBottomBarPreferences'];
  isDiscountsPanelCollapsed: UsePosUiState['isDiscountsPanelCollapsed'];
  setDiscountsPanelCollapsed: UsePosUiState['setDiscountsPanelCollapsed'];
  isPaymentsPanelCollapsed: UsePosUiState['isPaymentsPanelCollapsed'];
  setPaymentsPanelCollapsed: UsePosUiState['setPaymentsPanelCollapsed'];
}

export interface PosLayoutState {
  isMobile: boolean;
  layoutMode: UsePosUiState['layoutMode'];
  setLayoutMode: UsePosUiState['setLayoutMode'];
  bottomBar: {
    isPinned: boolean;
    showQuickActions: boolean;
    togglePinned: () => void;
    setPinned: (pinned: boolean) => void;
    toggleQuickActions: () => void;
    setQuickActions: (enabled: boolean) => void;
  };
  panels: {
    discounts: {
      collapsed: boolean;
      toggle: () => void;
      setCollapsed: (collapsed: boolean) => void;
    };
    payments: {
      collapsed: boolean;
      toggle: () => void;
      setCollapsed: (collapsed: boolean) => void;
    };
  };
}

export const usePosLayout = ({
  layoutMode,
  setLayoutMode,
  bottomBarPreferences,
  setBottomBarPreferences,
  isDiscountsPanelCollapsed,
  setDiscountsPanelCollapsed,
  isPaymentsPanelCollapsed,
  setPaymentsPanelCollapsed,
}: UsePosLayoutArgs): PosLayoutState => {
  const [viewportWidth, setViewportWidth] = useState<number>(() => {
    if (typeof window === 'undefined') {
      return MOBILE_BREAKPOINT;
    }
    return window.innerWidth;
  });

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    if (layoutMode !== 'auto') {
      return;
    }
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [layoutMode]);

  const isMobile = useMemo(() => {
    if (layoutMode === 'compact') {
      return true;
    }
    if (layoutMode === 'expanded') {
      return false;
    }
    return viewportWidth < MOBILE_BREAKPOINT;
  }, [layoutMode, viewportWidth]);

  const toggleDiscounts = useCallback(() => {
    setDiscountsPanelCollapsed(!isDiscountsPanelCollapsed);
  }, [isDiscountsPanelCollapsed, setDiscountsPanelCollapsed]);

  const togglePayments = useCallback(() => {
    setPaymentsPanelCollapsed(!isPaymentsPanelCollapsed);
  }, [isPaymentsPanelCollapsed, setPaymentsPanelCollapsed]);

  const togglePinned = useCallback(() => {
    setBottomBarPreferences({ isPinned: !bottomBarPreferences.isPinned });
  }, [bottomBarPreferences.isPinned, setBottomBarPreferences]);

  const toggleQuickActions = useCallback(() => {
    setBottomBarPreferences({ showQuickActions: !bottomBarPreferences.showQuickActions });
  }, [bottomBarPreferences.showQuickActions, setBottomBarPreferences]);

  const setPinned = useCallback(
    (pinned: boolean) => {
      setBottomBarPreferences({ isPinned: pinned });
    },
    [setBottomBarPreferences],
  );

  const setQuickActions = useCallback(
    (enabled: boolean) => {
      setBottomBarPreferences({ showQuickActions: enabled });
    },
    [setBottomBarPreferences],
  );

  return useMemo(
    () => ({
      isMobile,
      layoutMode,
      setLayoutMode,
      bottomBar: {
        isPinned: bottomBarPreferences.isPinned,
        showQuickActions: bottomBarPreferences.showQuickActions,
        togglePinned,
        setPinned,
        toggleQuickActions,
        setQuickActions,
      },
      panels: {
        discounts: {
          collapsed: isDiscountsPanelCollapsed,
          toggle: toggleDiscounts,
          setCollapsed: setDiscountsPanelCollapsed,
        },
        payments: {
          collapsed: isPaymentsPanelCollapsed,
          toggle: togglePayments,
          setCollapsed: setPaymentsPanelCollapsed,
        },
      },
    }),
    [
      bottomBarPreferences.isPinned,
      bottomBarPreferences.showQuickActions,
      isDiscountsPanelCollapsed,
      isMobile,
      isPaymentsPanelCollapsed,
      layoutMode,
      setDiscountsPanelCollapsed,
      setLayoutMode,
      setPaymentsPanelCollapsed,
      setPinned,
      setQuickActions,
      toggleDiscounts,
      togglePayments,
      togglePinned,
      toggleQuickActions,
    ],
  );
};

export default usePosLayout;
